import { EventDispatcher, Vector2 } from 'three';
import type { Vector3 } from 'three';
import {
  getEndTouchPosition,
  getPinchCenter,
  getTouchAsVector,
  getTouchDistance,
} from 'shared/utils/InputUtils';
import type SphereCameraControls from 'shared/renderingEngine/CameraControls';

const CLICK_MARGIN_OF_ERROR = 5;

const DOUBLE_TAP_DELAY = 200;

const SCALE_SPEED = 2e-3;

export interface TapEvent {
  type: 'tap';
  point: Vector2;
}

interface BlurEvent {
  type: 'blur';
}

export type TouchControlsEvent = TapEvent | BlurEvent;

export class TouchControls extends EventDispatcher<TouchControlsEvent> {
  private cameraControls: SphereCameraControls;

  private pointerIntersection?: Vector3;

  private startPointer?: Vector2;

  private previousTouchDistance = 0;

  private previousEvent?: TouchEvent;

  private pinched: boolean = false;

  private canvasWrapper?: HTMLElement;

  private lastTapTimestamp: number = 0;

  private doubleTapDragging: boolean = false;
  private doubleTapLastPoint = new Vector2();

  init(cameraControls: SphereCameraControls, canvasWrapper: HTMLElement) {
    this.cameraControls = cameraControls;
    this.canvasWrapper = canvasWrapper;

    this.onTouchStart = this.onTouchStart.bind(this);
    this.onTouchEnd = this.onTouchEnd.bind(this);
    this.onTouchMove = this.onTouchMove.bind(this);

    canvasWrapper?.addEventListener('touchstart', this.onTouchStart, { passive: false });
    canvasWrapper?.addEventListener('touchend', this.onTouchEnd, { capture: true });
    canvasWrapper?.addEventListener('touchmove', this.onTouchMove, { passive: true });
  }

  private onTouchStart(event: TouchEvent) {
    event.preventDefault();

    this.cameraControls.clearAnimations();
    this.startPointer = getTouchAsVector(event);

    this.doubleTapDragging = false;
    this.doubleTapLastPoint.copy(this.startPointer);

    let pointer = this.startPointer;

    if (event.timeStamp - this.lastTapTimestamp < DOUBLE_TAP_DELAY && event.touches.length === 1) {
      this.doubleTapDragging = true;
      this.previousTouchDistance = 0;
    }
    this.lastTapTimestamp = event.timeStamp;

    if (event.touches.length > 1) {
      this.previousTouchDistance = getTouchDistance(event.touches[0], event.touches[1]);
      pointer = getPinchCenter(event.touches[0], event.touches[1]);
    }

    this.pointerIntersection = this.cameraControls.sphereCamera.getMouseIntersection(
      pointer.x,
      pointer.y,
    );
    this.previousEvent = event;
  }

  private onTouchMove(event: TouchEvent) {
    if (!this.startPointer) {
      return;
    }
    this.cameraControls.clearAnimations();
    if (this.previousEvent && this.previousEvent.touches.length !== event.touches.length) {
      this.onTouchStart(event);
      return;
    }

    if (this.doubleTapDragging) {
      this.doubleTapZoom(event);
    } else if (event.touches.length > 1) {
      this.pinch(event);
      this.pinched = true;
    } else {
      const latestPointer = getTouchAsVector(event);
      const newIntersection = this.cameraControls.sphereCamera.getMouseIntersection(
        latestPointer.x,
        latestPointer.y,
      );

      if (newIntersection && this.pointerIntersection) {
        this.dispatchEvent({ type: 'blur' });
        this.cameraControls.move(newIntersection, this.pointerIntersection);
      }
    }

    this.previousEvent = event;
  }

  private onTouchEnd(event: TouchEvent) {
    if (!this.startPointer) {
      return;
    }
    this.doubleTapDragging = false;
    if (this.isTap(event) && event.changedTouches.length > 0) {
      const touch = event.changedTouches[0];
      this.dispatchEvent({
        type: 'tap',
        point: new Vector2(touch.clientX, touch.clientY),
      });
      event.preventDefault();
    } else {
      event.stopPropagation();
    }
    if (event.touches.length === 0) {
      this.pinched = false;
      this.cameraControls.endCameraDrag();
      this.startPointer = undefined;
      this.previousEvent = undefined;
    }
  }

  private doubleTapZoom(event: TouchEvent) {
    if (this.startPointer === undefined) return;
    const endPoint = getTouchAsVector(event);
    const distance = SCALE_SPEED * (this.startPointer.y - endPoint.y);
    const diff = this.doubleTapLastPoint.clone().sub(endPoint);
    this.doubleTapLastPoint.copy(endPoint);

    if (Math.abs(diff.x) > Math.abs(diff.y)) {
      return;
    }

    const zoomScaleFactor = 2 ** (distance - this.previousTouchDistance);
    this.cameraControls.zoomIntoPoint(this.startPointer, zoomScaleFactor);
    this.previousTouchDistance = distance;
  }

  private pinch(event: TouchEvent) {
    const currentTouchDistance = getTouchDistance(event.touches[0], event.touches[1]);
    const pinchCenter = getPinchCenter(event.touches[0], event.touches[1]);
    const zoomScaleFactor = this.previousTouchDistance / currentTouchDistance;

    this.cameraControls.zoomIntoPoint(pinchCenter, zoomScaleFactor);
    this.previousTouchDistance = currentTouchDistance;
    this.dispatchEvent({ type: 'blur' });
  }

  private isTap(event: TouchEvent) {
    if (this.pinched || event.touches.length > 0) {
      return false;
    }
    const currentPointer = getEndTouchPosition(event);
    const moveDistance = this.startPointer?.distanceTo(currentPointer);

    if (moveDistance === undefined) return true;

    return moveDistance < CLICK_MARGIN_OF_ERROR;
  }

  dispose() {
    const canvasWrapper = this.canvasWrapper;
    canvasWrapper?.removeEventListener('touchstart', this.onTouchStart);
    canvasWrapper?.removeEventListener('touchend', this.onTouchEnd); // TODO: this doesn't get disposed somehow
    canvasWrapper?.removeEventListener('touchmove', this.onTouchMove);
  }
}
